import React from "react";
import { Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useAuthStore } from "../store/useAuthStore";

/**
 * SessionExpiredDialog
 *
 * Shown when the session ends without an explicit user logout — idle timeout, expired
 * refresh token, or any auth error that force-logged the user out (see axiosClient and
 * useIdleTimeout). The store is already logged out by the time this opens; the single
 * "Re-login" action clears the flag and sends the user to the login page.
 */
const SessionExpiredDialog: React.FC = () => {
  const sessionExpired = useAuthStore((s) => s.sessionExpired);
  const message = useAuthStore((s) => s.sessionExpiredMessage);
  const setSessionExpired = useAuthStore((s) => s.setSessionExpired);

  const handleRelogin = () => {
    setSessionExpired(false);
    // Full navigation: tokens are already cleared, so "/" renders the login page.
    window.location.href = "/";
  };

  return (
    <Dialog open={sessionExpired} onOpenChange={(open) => { if (!open) handleRelogin(); }}>
      <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-destructive" />
            Session Expired
          </DialogTitle>
          <DialogDescription>
            {message ?? "Your session has expired. Please log in again."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button onClick={handleRelogin} className="w-full sm:w-auto">
            Login Again
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SessionExpiredDialog;
